import {
  ADDITIONAL_FILTERS_BY_FEATURE_FLAG,
  PROGRAM_FILTER_REQUEST_PARAM,
  SUBJECT_FILTER_REQUEST_PARAM
} from "../constants"
import { ProgramAndCoursesInfo } from "../../../types/gqlTypes/graphql"
import {
  getProgramCourseMapping,
  getProgramCoursesOptions
} from "../../snapcourse/authoring/components/SnapCourseConfigForm/components/ProgramCourseDropdown/helpers"
import { FILTER_OPTIONS } from "../constants/filters"
import {
  WORKKEYS_REPLACE_FILTER_KEYS,
  WORKKEYS_REPLACE_SEARCH_PARAMS
} from "../constants/workkeysReplaceTerms"
import {
  getWorkkeysReplaceQueryContentType,
  getWorkkeysReplaceQueryContentTypeReverse
} from "./workkeysReplaceTerms"
import {
  addTimeToDate,
  formatLocalDateToUTCDate
} from "../../mp-core/helpers/formatters/date"
import { CubeRequestFilter, CubeRequestParams } from "../types"
import { IndentationDropdownOptions } from "../../mp-shared-components/generic-shared-components/components/IndentationDropdown"
import {
  FeatureFlagName,
  FeatureFlagStatesType
} from "../../mp-utils/generic-utils/feature-flags/types"
import { FiltersState } from "../states"

export const ORG_FILTERS_MAP = {
  organization: ["org_view_organizations.organizationId", "organizationId"],
  state: ["org_view_states.code", "state"],
  district: ["org_view_districts.districtId", "districtId"],
  school: ["org_view_schools.schoolId", "schoolId"],
  teacher: ["org_view_teachers.teacherId", "teacherId"],
  class: ["org_view_classes.classId", "classId"],
  student: ["org_view_students.studentId", "studentId"],
  userStatus: ["org_view_students.isActive", "isActive"],
  grade: ["org_view_students.grade", "grade"],
  adminRole: ["org_view_admins.role", "role"]
}

/**
 * For certain filters we need to redefine base map.
 */
export const ORG_FILTERS_MAP_BY_FILTER = {
  student: {
    teacher: ["org_view_teachers.teacherId", "teacherIds"],
    class: ["org_view_classes.classId", "classIds"]
  }
}

/**
 * Map data from filters to cube views.
 * @param source Data source to pull data from.
 */
export const getOrgFiltersMap = (
  source: string,
  externalMap?: Record<string, string[]>
): Record<string, string> => {
  return Object.values({ ...ORG_FILTERS_MAP, ...(externalMap ?? {}) }).reduce(
    (acc, [from, to]) => ({
      ...acc,
      [from]: `${source}.${to}`
    }),
    {}
  )
}

/**
 * Map data from filters to cube views.
 * @param source Data source to pull data from.
 * @param certainFilters Array of filters keys that can be returned from ORG_FILTERS_MAP
 */
export const getCertainOrgFiltersMap = (
  source: string,
  certainFilters: string[],
  externalMap?: Record<string, string[]>
): Record<string, string> => {
  if (!certainFilters?.length) return {}

  return Object.entries({
    ...ORG_FILTERS_MAP,
    ...(externalMap ?? {})
  }).reduce((acc, [filterKey, [from, to]]) => {
    if (!certainFilters.includes(filterKey)) return acc

    return {
      ...acc,
      [from]: `${source}.${to}`
    }
  }, {})
}

/**
 * Transformation of ProgramAndCoursesInfo to dropdown options.
 * @param programAndCourses List of programs with courses and program info.
 * @param config Optional configuration for filtering behavior
 */
export const getTestFilterOptions = (
  programAndCourses: ProgramAndCoursesInfo[],
  config?: { excludeCourses?: boolean }
): IndentationDropdownOptions[] => {
  const dropdownOptions = programAndCourses?.map(
    ({ program, courses, programInfo }) => ({
      program,
      courses: config?.excludeCourses
        ? []
        : (courses.map(i => i.name) as string[]),
      programDisplayName: programInfo.displayName
    })
  )

  const options = getProgramCoursesOptions(dropdownOptions ?? [], {
    selectableProgram: true
  })

  return options
}

export const getTestFilterRequest = (
  values: string[],
  filterMemberMap?: Record<string, string>
) => {
  const programDimension = filterMemberMap?.[PROGRAM_FILTER_REQUEST_PARAM]

  if (!programDimension) {
    return null
  }

  const subjectDimension = filterMemberMap?.[SUBJECT_FILTER_REQUEST_PARAM]

  // If we don't have `subject` dimension, then create request only with uniq programs
  if (!subjectDimension) {
    return {
      member: programDimension,
      operator: "equals",
      values: [...Array.from(new Set(values.map(item => item.split("_")[0])))]
    }
  }

  // Create object from `test` filter array values to Record<`program`, `subject`[]>
  const programsSubjectsMapping = getProgramCourseMapping(values)

  // Create filter request with programs and their own subjects
  const programsAndSubjectsFilters = Object.entries(
    programsSubjectsMapping
  ).map(([program, subjects]) => {
    if (!subjects.length) {
      return {
        member: programDimension,
        operator: "equals",
        values: [program]
      }
    }

    return {
      and: [
        {
          member: programDimension,
          operator: "equals",
          values: [program]
        },
        {
          member: subjectDimension,
          operator: "equals",
          values: subjects
        }
      ]
    }
  })

  if (!programsAndSubjectsFilters.length) return {}

  // If only one program in filter, then we don't need to add `or` prefix
  return programsAndSubjectsFilters.length > 1
    ? {
        or: programsAndSubjectsFilters
      }
    : programsAndSubjectsFilters[0]
}

export const getScoreRangeFilters = (
  value: string[],
  filterMemberMap: Record<string, string>
) => {
  if (!value) {
    return { testFilter: null, scoreFilters: [] }
  }

  const [testValue, scoreRange] = value[0].split("::")
  const programSubjects = testValue.split("/")
  const [min, max] = scoreRange.split("-")

  const testFilter = getTestFilterRequest(programSubjects, filterMemberMap)
  const scoreFilters = [
    {
      member: filterMemberMap?.score,
      operator: "gte",
      values: [min]
    },
    {
      member: filterMemberMap?.score,
      operator: "lte",
      values: [max]
    }
  ]

  return { testFilter, scoreFilters }
}

export const getClearInitialSearchQuery = (
  searchQuery: Record<string, string | string[]>,
  userStaticData: Record<string, string[]>,
  featureFlags: FeatureFlagStatesType
) => {
  return Object.fromEntries(
    Object.entries(searchQuery)
      // Remove filters that can change user's static data (user's static data can't be changes)
      .filter(([key, _value]) => !userStaticData?.[key])
      // Remove incorrect filters values for filters that have static options
      .map(item => {
        const [key, filterValue] = item

        if (!FILTER_OPTIONS[key]) return item

        // Flat array of filter's options
        let filterOptionsValues = FILTER_OPTIONS[key].reduce((res, option) => {
          return [
            ...res,
            option.value,
            ...(!!option.optionDetails?.children
              ? option.optionDetails?.children
              : [])
          ]
        }, [])

        // Updated filter's values for url search
        const updatedFilterValues = Array.isArray(filterValue)
          ? filterValue.filter(value => filterOptionsValues.includes(value))
          : filterOptionsValues.includes(filterValue)
          ? filterValue
          : []

        return [key, updatedFilterValues]
      })
  )
}

export const getDateFilterByMaxLimit = (
  dateRange: string[],
  requestField: string
) => {
  const rightDateLimit = dateRange?.[1]

  if (!rightDateLimit) return []

  return [
    {
      member: requestField,
      operator: "beforeOrOnDate",
      values: [
        formatLocalDateToUTCDate({
          date: rightDateLimit,
          withTime: true,
          withSeconds: true,
          useEndOfDay: true,
          useISOFormat: true
        })
      ]
    }
  ]
}

export const getCacheDisableFilter = (member: string): CubeRequestFilter[] => {
  if (!member) return []

  return [
    {
      values: [
        formatLocalDateToUTCDate({
          date: addTimeToDate({
            date: new Date(),
            timeToAdd: { hours: 1 }
          }),
          withTime: true,
          withSeconds: true,
          useISOFormat: true
        })
      ],
      member,
      operator: "beforeOrOnDate"
    }
  ]
}

export const getAdditionalFeatureFlagFilters = (args: {
  page: string
  subpage: string
  filters: FiltersState
  featureFlags: FeatureFlagStatesType
  reportingQuestionsProgramsAndCourses: ProgramAndCoursesInfo[]
}): CubeRequestParams["filters"] => {
  const {
    page,
    subpage,
    filters,
    featureFlags,
    reportingQuestionsProgramsAndCourses
  } = args

  if (
    page === "content" &&
    subpage === "content-library" &&
    !filters["contentType"]
  ) {
    const contentFeatureFlag =
      FeatureFlagName.reporting_content_library_skill_review_mastery_curriculum

    return !featureFlags?.[contentFeatureFlag]
      ? ADDITIONAL_FILTERS_BY_FEATURE_FLAG?.[page]?.[subpage]?.[
          contentFeatureFlag
        ] ?? []
      : []
  }

  if (
    page === "insights" &&
    ["questions", "studentQuestions"].includes(subpage) &&
    !filters?.test?.length &&
    !featureFlags?.[
      FeatureFlagName.reporting_insights_performance_questions_show_all_programs
    ]
  ) {
    return !!reportingQuestionsProgramsAndCourses?.length
      ? [
          {
            member: "questions_view.program",
            operator: "equals",
            values: [
              ...reportingQuestionsProgramsAndCourses.map(item => item.program)
            ]
          }
        ]
      : []
  }

  return []
}

export const replaceKeysInFiltersObject = (
  filters: Record<string, string | string[]>,
  keysMapping: Record<string, string>
) => {
  if (!Object.keys(filters || {}).some(key => !!keysMapping[key])) {
    return filters
  }

  return Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [
      keysMapping[key] || key,
      value
    ])
  )
}

export const replaceWorkkeysSearchParamsToReadyFilter = (
  searchParams: Record<string, string | string[]>
) => {
  const replacedKeys = replaceKeysInFiltersObject(
    searchParams,
    WORKKEYS_REPLACE_SEARCH_PARAMS
  )

  // Replace contentType values (from WorkKeys to Ready format)
  if (replacedKeys.contentType) {
    const contentTypeValue = replacedKeys.contentType
    if (Array.isArray(contentTypeValue)) {
      replacedKeys.contentType = contentTypeValue.map(value =>
        getWorkkeysReplaceQueryContentTypeReverse(String(value))
      )
    } else {
      replacedKeys.contentType = getWorkkeysReplaceQueryContentTypeReverse(
        String(contentTypeValue)
      )
    }
  }

  return replacedKeys
}

export const replaceReadyFilterToWorkkeysSearchParams = (
  filters: Record<string, string | string[]>
) => {
  const replacedKeys = replaceKeysInFiltersObject(
    filters,
    WORKKEYS_REPLACE_FILTER_KEYS
  )

  // Replace contentType values (from Ready to WorkKeys format)
  if (replacedKeys.contentType) {
    const contentTypeValue = replacedKeys.contentType
    if (Array.isArray(contentTypeValue)) {
      replacedKeys.contentType = contentTypeValue.map(value =>
        getWorkkeysReplaceQueryContentType(String(value))
      )
    } else {
      replacedKeys.contentType = getWorkkeysReplaceQueryContentType(
        String(contentTypeValue)
      )
    }
  }

  return replacedKeys
}

export const getOrgActiveWidgetDynamicFilters =
  (requestView: string) => (requestIndex: number, filters: FiltersState) => {
    let filtersRequests = []

    if (!!filters?.date) {
      filtersRequests.push(
        ...getDateFilterByMaxLimit(filters.date, `${requestView}.createdAt`)
      )
    }

    if (requestIndex === 0 && filters?.userStatus?.length === 1) {
      filtersRequests.push({
        member: `${requestView}.isActive`,
        operator: "equals",
        values: [...filters.userStatus]
      })
    }

    return filtersRequests
  }
